/**
 * Simuladores deterministas (FR-SIM-1, FR-SIM-2, FR-SIM-3, FR-SIM-4).
 * Toda cantidad monetaria es bigint en unidades minimas; la tasa por periodo
 * se expresa en "micros" (unidad = 1/1_000_000) para evitar por completo el
 * punto flotante binario (NFR-MONEY-1), incluida la amortizacion, calculada
 * por biseccion entera en vez de la formula cerrada de anualidad.
 */

const RATE_SCALE = 1_000_000n;

function periodInterest(balance: bigint, ratePerPeriodMicros: bigint): bigint {
  return (balance * ratePerPeriodMicros) / RATE_SCALE; // truncado hacia cero, deterministico
}

// ---------------------------------------------------------------- FR-SIM-1

export interface CompoundInterestResult {
  readonly finalBalance: bigint;
  readonly totalContributed: bigint;
  readonly totalInterest: bigint;
}

export function simulateCompoundInterest(
  principalMinor: bigint,
  contributionMinor: bigint,
  ratePerPeriodMicros: bigint,
  periods: number,
): CompoundInterestResult {
  if (periods < 0) throw new RangeError("periods no puede ser negativo");
  let balance = principalMinor;
  let totalContributed = 0n;
  let totalInterest = 0n;
  for (let i = 0; i < periods; i++) {
    const interest = periodInterest(balance, ratePerPeriodMicros);
    balance += interest + contributionMinor;
    totalInterest += interest;
    totalContributed += contributionMinor;
  }
  return { finalBalance: balance, totalContributed, totalInterest };
}

// ---------------------------------------------------------------- FR-SIM-2

export interface AmortizationRow {
  readonly period: number;
  readonly payment: bigint;
  readonly interest: bigint;
  readonly principalPaid: bigint;
  readonly balance: bigint;
}

function balanceAfterFixedPayment(
  principalMinor: bigint,
  ratePerPeriodMicros: bigint,
  payment: bigint,
  periods: number,
): bigint {
  let balance = principalMinor;
  for (let i = 0; i < periods; i++) {
    balance += periodInterest(balance, ratePerPeriodMicros) - payment;
  }
  return balance;
}

/** Cuota nivelada minima tal que el saldo al final del horizonte es <= 0, hallada por biseccion entera. */
export function computeLevelPayment(
  principalMinor: bigint,
  ratePerPeriodMicros: bigint,
  periods: number,
): bigint {
  if (periods <= 0) throw new RangeError("periods debe ser positivo");
  let lo = 0n;
  let hi = principalMinor + (principalMinor * ratePerPeriodMicros * BigInt(periods)) / RATE_SCALE + 1n;
  while (lo < hi) {
    const mid = (lo + hi) / 2n;
    if (balanceAfterFixedPayment(principalMinor, ratePerPeriodMicros, mid, periods) > 0n) {
      lo = mid + 1n;
    } else {
      hi = mid;
    }
  }
  return lo;
}

/**
 * Cuadro de amortizacion. El ultimo periodo salda el saldo restante
 * exactamente: la suma de principalPaid de todas las filas es, por
 * construccion, igual al importe del prestamo (FR-SIM-2).
 */
export function amortizationSchedule(
  principalMinor: bigint,
  ratePerPeriodMicros: bigint,
  periods: number,
): AmortizationRow[] {
  const payment = computeLevelPayment(principalMinor, ratePerPeriodMicros, periods);
  const rows: AmortizationRow[] = [];
  let balance = principalMinor;
  for (let period = 1; period <= periods; period++) {
    const interest = periodInterest(balance, ratePerPeriodMicros);
    const isLast = period === periods;
    const principalPaid = isLast ? balance : payment - interest;
    const actualPayment = isLast ? principalPaid + interest : payment;
    balance -= principalPaid;
    rows.push({ period, payment: actualPayment, interest, principalPaid, balance });
  }
  return rows;
}

// ---------------------------------------------------------------- FR-SIM-3

/**
 * Numero de periodos hasta alcanzar targetMinor. Con aportacion cero y tasa
 * no positiva, el resultado es inalcanzable (null), nunca infinito ni error.
 */
export function periodsToSavingsGoal(
  startingMinor: bigint,
  contributionMinor: bigint,
  ratePerPeriodMicros: bigint,
  targetMinor: bigint,
  maxPeriods = 100_000,
): number | null {
  if (startingMinor >= targetMinor) return 0;
  if (contributionMinor <= 0n && ratePerPeriodMicros <= 0n) return null; // inalcanzable, declarado explicitamente
  let balance = startingMinor;
  for (let period = 1; period <= maxPeriods; period++) {
    balance += periodInterest(balance, ratePerPeriodMicros) + contributionMinor;
    if (balance >= targetMinor) return period;
  }
  return null; // fuera del horizonte considerado: inalcanzable en ese plazo, no un error
}
